import React, { useEffect, useMemo, useState, useRef } from "react";
import { useRefinementList } from "react-instantsearch";

const COMPANIES = [
  { key: "bice_bank",        label: "Banco BICE",        src: () => RAILS_ASSET_URL("/images/companies/BANCO_BICE_AZUL.png") },
  { key: "bice_investments", label: "BICE Inversiones",  src: () => RAILS_ASSET_URL("/images/companies/BICE_INVERSIONES_GRIS.png") },
  { key: "bice_corp",        label: "BICE Corp",         src: () => RAILS_ASSET_URL("/images/companies/BICE_CORP_AZUL.png") },
  { key: "bk",               label: "BK Servicios",      src: () => RAILS_ASSET_URL("/images/companies/BK_AZUL.png") }
];

export default function CompanyFilterBar() {
  const { items, refine } = useRefinementList({
    attribute: "requesting_company",
    limit: 100
  });
  
  // Track pending selection to handle async refine operations
  const [pendingSelection, setPendingSelection] = useState(null);
  const isProcessingRef = useRef(false);
  const isClearingRef = useRef(false);
  
  // Track selected company locally (even if it doesn't have results)
  const [localSelectedCompany, setLocalSelectedCompany] = useState(null);

  // Log items for debugging
  useEffect(() => {
    console.log('[CompanyFilterBar] Items from useRefinementList:', JSON.stringify(items, null, 2));
    const refined = items.filter(item => item.isRefined);
    console.log('[CompanyFilterBar] Refined items:', JSON.stringify(refined, null, 2));
    console.log('[CompanyFilterBar] All items details:');
    items.forEach((item, index) => {
      console.log(`  Item ${index}:`, {
        label: item.label,
        value: item.value,
        isRefined: item.isRefined,
        count: item.count,
        labelType: typeof item.label,
        valueType: typeof item.value
      });
    });
  }, [items]);

  // Sync local selection with InstantSearch items (only when items change, not localSelectedCompany)
  useEffect(() => {
    // Don't sync if we're currently clearing
    if (isClearingRef.current) {
      console.log(`[CompanyFilterBar] Skipping sync - currently clearing`);
      return;
    }
    
    const refinedItems = items.filter(item => item.isRefined);
    if (refinedItems.length > 0) {
      // Update local selection based on InstantSearch state
      const refinedKey = refinedItems[0].label || refinedItems[0].value;
      if (refinedKey !== localSelectedCompany) {
        console.log(`[CompanyFilterBar] Syncing local selection with InstantSearch:`, refinedKey);
        setLocalSelectedCompany(refinedKey);
      }
    } else if (localSelectedCompany && refinedItems.length === 0 && !pendingSelection) {
      // If InstantSearch has no refined items but we have a local selection,
      // and there's no pending selection, it means the filter was cleared externally
      // Only clear if it's not a company without results (those won't appear in items)
      // We'll keep the local selection for companies without results
      const companyHasResults = items.some(item => 
        (item.label === localSelectedCompany || item.value === localSelectedCompany)
      );
      if (companyHasResults) {
        console.log(`[CompanyFilterBar] Clearing local selection (filter was cleared externally)`);
        setLocalSelectedCompany(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Memoize selected state to avoid recalculating on every render
  const selectedKeys = useMemo(() => {
    return new Set(
      items
        .filter(item => item.isRefined)
        .map(item => item.label || item.value)
    );
  }, [items]);

  const isSelected = (key) => {
    // Check both InstantSearch state and local state
    const instantSearchSelected = selectedKeys.has(key) || items.some(item => 
      (item.label === key || item.value === key) && item.isRefined
    );
    const localSelected = localSelectedCompany === key;
    return instantSearchSelected || localSelected;
  };

  // Handle pending selection when items change
  useEffect(() => {
    if (pendingSelection && !isProcessingRef.current) {
      isProcessingRef.current = true;
      const key = pendingSelection;
      
      console.log(`[CompanyFilterBar] Processing pending selection:`, key);
      
      // Check if the item now exists in aggregations
      const item = items.find(item => {
        const labelMatch = String(item.label) === String(key);
        const valueMatch = String(item.value) === String(key);
        return labelMatch || valueMatch;
      });
      
      // Check if there are any refined items that need to be cleared first
      const currentlyRefined = items.filter(item => item.isRefined);
      
      console.log(`[CompanyFilterBar] Currently refined items:`, currentlyRefined.length);
      console.log(`[CompanyFilterBar] Found matching item:`, !!item);
      
      if (currentlyRefined.length > 0) {
        // Still need to clear first - wait for next render
        console.log(`[CompanyFilterBar] Still have refined items, will retry...`);
        isProcessingRef.current = false;
        return;
      }
      
      // Now we can apply the selection
      // Always use refine - it should work even if the value doesn't exist in aggregations
      // The adapter will handle the filter correctly
      console.log(`[CompanyFilterBar] Applying refinement with key:`, key);
      refine(key);
      setLocalSelectedCompany(key);
      
      setPendingSelection(null);
      isProcessingRef.current = false;
    }
  }, [items, pendingSelection, refine]);

  const handleSelect = (key) => {
    console.log(`[CompanyFilterBar] ===== handleSelect called =====`);
    console.log(`[CompanyFilterBar] Key to select:`, key, `(type: ${typeof key})`);
    console.log(`[CompanyFilterBar] Current items count:`, items.length);
    
    // Log all items with full details
    items.forEach((item, index) => {
      console.log(`[CompanyFilterBar] Item ${index}:`, {
        label: item.label,
        value: item.value,
        isRefined: item.isRefined,
        count: item.count,
        labelMatches: item.label === key,
        valueMatches: item.value === key,
        labelType: typeof item.label,
        valueType: typeof item.value
      });
    });
    
    // Check if already selected
    const isCurrentlySelected = isSelected(key);
    console.log(`[CompanyFilterBar] Currently selected:`, isCurrentlySelected);
    
    // If clicking the same item that's already selected, deselect it
    if (isCurrentlySelected) {
      console.log(`[CompanyFilterBar] Item already selected, deselecting...`);
      const item = items.find(item => {
        const labelMatch = String(item.label) === String(key);
        const valueMatch = String(item.value) === String(key);
        return (labelMatch || valueMatch) && item.isRefined;
      });
      if (item) {
        console.log(`[CompanyFilterBar] Deselecting item:`, item.value);
        refine(item.value);
      } else {
        // Item is selected locally but not in InstantSearch (no results)
        console.log(`[CompanyFilterBar] Deselecting local selection:`, key);
        refine(key); // Try to refine to toggle it off
      }
      setLocalSelectedCompany(null);
      return;
    }
    
    // First, clear any currently selected items
    const currentlyRefined = items.filter(item => item.isRefined);
    if (currentlyRefined.length > 0) {
      console.log(`[CompanyFilterBar] Clearing ${currentlyRefined.length} currently refined item(s)...`);
      currentlyRefined.forEach(item => {
        console.log(`[CompanyFilterBar] Clearing:`, item.value);
        refine(item.value);
      });
      // Set pending selection to apply after clear
      setPendingSelection(key);
    } else {
      // No items to clear, apply directly
      const item = items.find(item => {
        const labelMatch = String(item.label) === String(key);
        const valueMatch = String(item.value) === String(key);
        return labelMatch || valueMatch;
      });
      
      // Always use refine - it should work even if the value doesn't exist in aggregations
      // The adapter will handle the filter correctly
      if (item) {
        console.log(`[CompanyFilterBar] ✓ Found matching item, refining with item.value:`, item.value);
        refine(item.value);
      } else {
        console.log(`[CompanyFilterBar] ✗ Item not found (no results), refining directly with key:`, key);
        refine(key);
      }
      setLocalSelectedCompany(key);
    }
    console.log(`[CompanyFilterBar] ===== handleSelect end =====`);
  };

  const handleClear = () => {
    console.log('[CompanyFilterBar] ===== handleClear called =====');
    
    // Set clearing flag to prevent sync from interfering
    isClearingRef.current = true;
    
    // Save local selection before clearing
    const companyToClear = localSelectedCompany;
    
    // Clear any pending selection first
    if (pendingSelection) {
      console.log('[CompanyFilterBar] Clearing pending selection:', pendingSelection);
      setPendingSelection(null);
      isProcessingRef.current = false;
    }
    
    // Clear local selection immediately
    if (companyToClear) {
      console.log('[CompanyFilterBar] Clearing local selection:', companyToClear);
      setLocalSelectedCompany(null);
    }
    
    const refinedItems = items.filter(item => item.isRefined);
    console.log('[CompanyFilterBar] Found refined items to clear:', refinedItems.length);
    console.log('[CompanyFilterBar] Refined items:', JSON.stringify(refinedItems, null, 2));
    
    // Clear each refined item from InstantSearch
    // Only call refine once per item - refine is a toggle, so calling it on a refined item will unrefine it
    refinedItems.forEach((item, index) => {
      console.log(`[CompanyFilterBar] Clearing item ${index}:`, item.value, `(isRefined: ${item.isRefined})`);
      if (item.isRefined) {
        refine(item.value);
      }
    });
    
    // If we have a local selection that's not in items (company with no results),
    // try to clear it by calling refine (which should toggle it off)
    if (companyToClear) {
      const itemExists = items.some(item => 
        (item.label === companyToClear || item.value === companyToClear) && item.isRefined
      );
      
      if (!itemExists) {
        // Company has no results or is not refined - try to clear it
        console.log(`[CompanyFilterBar] Clearing local selection (no results or not refined):`, companyToClear);
        refine(companyToClear);
        // Force a search refresh to ensure the filter is cleared
        setTimeout(() => {
          isClearingRef.current = false;
          window.dispatchEvent(new Event('search:refresh'));
        }, 100);
      } else {
        // Item exists and is refined - it was already cleared in the forEach above
        console.log(`[CompanyFilterBar] Item already being cleared in forEach`);
        setTimeout(() => {
          isClearingRef.current = false;
        }, 100);
      }
    } else {
      // No local selection to clear
      setTimeout(() => {
        isClearingRef.current = false;
      }, 100);
    }
    
    console.log('[CompanyFilterBar] ===== handleClear end =====');
  };

  // Check if there's a selection (either in InstantSearch or locally)
  const hasSelection = items.some(item => item.isRefined) || localSelectedCompany !== null;

  return (
    <div className="company-filter-bar py-3">
      <div className="d-flex justify-content-center align-items-center flex-wrap gap-4">
        {COMPANIES.map((c) => {
          const selected = isSelected(c.key);
          const disabled = selected; // Disable only if this company is already selected
          return (
            <button
              key={c.key}
              type="button"
              title={disabled ? "Esta compañía ya está seleccionada. Selecciona otra para cambiar." : c.label}
              aria-pressed={selected}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={() => !disabled && handleSelect(c.key)}
              className={
                "btn p-2 rounded-3 company-logo-btn " +
                (selected ? "border border-primary-subtle shadow-sm" : "border-0") +
                (disabled ? " opacity-75" : "")
              }
              style={{ 
                lineHeight: 0,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.75 : 1
              }}
            >
              <img
                src={c.src()}
                alt={c.label}
                className="company-logo-img"
                height="56"
                style={{ opacity: disabled ? 0.75 : 1 }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </button>
          );
        })}
      </div>

      {hasSelection && (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClear}
          >
            Ver todas
          </button>
        </div>
      )}
    </div>
  );
}
